import {defineField, defineType} from 'sanity'
import {BsBorderAll} from 'react-icons/bs'

import {em, strikeThrough, strong, superscript, underline} from '../../data/portableTextDecorators'
import {externalLink, internalLink, textColor} from '../../data/portableTextAnnotations'

/**
 * AR-1823 - canonical table type for the Studio's built-in Portable Text table
 * editor (shipped in Studio 6.5.0, powered by @portabletext/plugin-table).
 *
 * Named `richTable` rather than `table` because the legacy @sanity/table plugin
 * already owns the `table` type name. That legacy shape is a flat
 * `rows[].cells[]` of plain strings and is still used by tableBlock,
 * compareTable, sizeTable, compare and buyerGuide - none of which this type
 * touches. The plugin is bound to the names below via `containers` in
 * sanity.config.ts.
 *
 * `headerRows` MUST stay declared here: the editor strips fields the schema does
 * not declare, so without it the table menu's header-row toggle silently does
 * nothing - no error, no warning, and the first row never renders as a header.
 *
 * Cell content is a restricted block subset: basic decorators, bullet and
 * numbered lists, and the internal/external link and colour annotations. No
 * images and no nested tables - the renderer relies on cells not containing
 * another table, and the sync layer relies on cells holding no references other
 * than internalLink.
 *
 * `internalLink` works in cells only because of a matching change in the sync
 * layer. Resolving internalLink to an href walks the document tree under a hard
 * depth cap, and a cell's markDefs sit eight levels below the table, which put
 * them past the cap in every host field except a blog article body. A
 * `richTable` node now restarts that budget for its own subtree - see
 * DEPTH_RESET_TYPE in frontend/server/sync/sync.transforms.ts.
 *
 * Cell links are resolved in two places, and BOTH key off this type name:
 *   1. Primary - the `_type == 'richTable'` clause inside PAGEBLOCKS_GROQ
 *      (frontend/server/sync/groq/sync.groq.projections.ts), which resolves the
 *      href at query time for every endpoint that ships pageBlocks, including
 *      the wheel-line and accessory endpoints that never run the runtime walk.
 *   2. Fallback - DEPTH_RESET_TYPE in sync.transforms.ts, for the content/**
 *      endpoints and anything resolved at request time.
 *
 * Renaming this type without renaming both of those will not fail a build or a
 * test - internal links in cells will simply go dead again, silently.
 */
const richTableCellContent = [
  {
    type: 'block',
    styles: [{title: 'Normal', value: 'normal'}],
    lists: [
      {title: 'Bullet', value: 'bullet'},
      {title: 'Numbered', value: 'number'},
    ],
    marks: {
      decorators: [strong, em, superscript, underline, strikeThrough],
      annotations: [internalLink, externalLink, textColor],
    },
  },
]

export default defineType({
  name: 'richTable',
  title: 'Table',
  type: 'object',
  icon: BsBorderAll,
  fields: [
    defineField({
      name: 'headerRows',
      title: 'Header rows',
      type: 'number',
      description:
        'How many rows at the top of the table are headers. Managed by the table controls in the editor.',
      // Hidden from the form because the table controls own this value. Exposed as
      // a plain number input, setting it to the row count would put every row in
      // <thead>: all rows uppercase and bold, no zebra striping, no <tbody>.
      // `hidden` is presentational only - the plugin still patches the value.
      hidden: true,
      validation: (Rule: any) => Rule.min(0).integer(),
    }),
    defineField({
      name: 'rows',
      title: 'Rows',
      type: 'array',
      of: [
        {
          name: 'richTableRow',
          title: 'Row',
          type: 'object',
          fields: [
            defineField({
              name: 'cells',
              title: 'Cells',
              type: 'array',
              of: [
                {
                  name: 'richTableCell',
                  title: 'Cell',
                  type: 'object',
                  fields: [
                    defineField({
                      name: 'content',
                      title: 'Content',
                      type: 'array',
                      of: richTableCellContent,
                    }),
                  ],
                },
              ],
            }),
          ],
        },
      ],
    }),
  ],
  preview: {
    select: {
      rows: 'rows',
      headerRows: 'headerRows',
    },
    prepare({rows, headerRows}) {
      const rowList = Array.isArray(rows) ? rows : []
      const columns = rowList.reduce(
        (max: number, row: any) => Math.max(max, Array.isArray(row?.cells) ? row.cells.length : 0),
        0,
      )

      if (!rowList.length || !columns) {
        return {title: 'Table', subtitle: 'Empty table'}
      }

      const headers = Number(headerRows)
      const parts = [
        `${rowList.length} ${rowList.length === 1 ? 'row' : 'rows'}`,
        `${columns} ${columns === 1 ? 'column' : 'columns'}`,
      ]
      if (Number.isFinite(headers) && headers > 0) {
        parts.push(`${headers} header ${headers === 1 ? 'row' : 'rows'}`)
      }

      return {title: 'Table', subtitle: parts.join(' - ')}
    },
  },
})
